import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectFilter } from '../../../../store/selectors/filters'
import { selectClientId, selectClientIsGuest } from '../../../../store/selectors/client'
import { RemoveSavedSearchButton } from './RemoveSavedSearchButton/RemoveSavedSearchButton'
import { ConfirmSavedSearchButton } from './ConfirmSavedSearchButton/ConfirmSavedSearchButton'
import isEqual from 'lodash/isEqual'
import { SavedSearchModel } from '../../../../types/savedSearches'
import { useGetSavedSearches } from '../../../../hooks/listings/useGetSavedSearches'
import { useRecoilState } from 'recoil'
import { currentSearchLocation } from '../../GoogleAutoComplete/state/currentSearchLocation'

export const SavedSearchToggle = () => {
  const clientIsGuest = useSelector(selectClientIsGuest)
  const filters = useSelector(selectFilter)
  const clientId = useSelector(selectClientId) as string
  const [searchLocation] = useRecoilState(currentSearchLocation)
  const [isCurrentSearchSaved, setIsCurrentSearchSaved] = useState(false)
  const [savedSearchMatchingCurrentFilters, setSavedSearchMatchingCurrentFilters] = useState<SavedSearchModel | null>(
    null
  )

  const { data } = useGetSavedSearches(
    {
      input: { clientId: clientId }
    },
    {
      enabled: !!clientId
    }
  )

  const isEqualOrNullish = (a: any, b: any): boolean => {
    return a === b || (a == null && b == null)
  }

  useEffect(() => {
    if (!clientIsGuest) {
      // compare saved search criteria to current search filters
      const existingSavedSearchThatMatchesCurrentFilters = data?.savedSearches?.find(
        savedSearch =>
          // for custom map area searches, the odds of two bounding boxes being identical are slim, so we'll only worry about city/zip/etc searches and just compare the placeId
          searchLocation?.place_id !== undefined &&
          savedSearch?.searchArea?.placeIdInput?.placeId === searchLocation?.place_id &&
          savedSearch?.filters.hasSellerConcession === !!filters.hasSellerConcession &&
          savedSearch?.filters.minAcres === filters.minAcres &&
          savedSearch?.filters.maxAcres === filters.maxAcres &&
          savedSearch?.filters.isAssumable === !!filters.isAssumable &&
          savedSearch?.filters.minPrice === filters.minPrice &&
          savedSearch?.filters.maxPrice === filters.maxPrice &&
          savedSearch?.filters.minSqft === filters.minSqft &&
          savedSearch?.filters.maxSqft === filters.maxSqft &&
          // the filters treat years as strings
          savedSearch?.filters.minYearBuilt === (filters.minYearBuilt ? Number(filters.minYearBuilt) : null) &&
          savedSearch?.filters.maxYearBuilt === (filters.maxYearBuilt ? Number(filters.maxYearBuilt) : null) &&
          savedSearch?.filters.minBedroomsCount === filters.minBedroomsCount &&
          savedSearch?.filters.minBathroomsCount === filters.minBathroomsCount &&
          isEqualOrNullish(
            savedSearch?.filters.monthlyExpensesInput?.downpaymentAmountDollars,
            filters.monthlyExpensesInput?.downpaymentAmountDollars
          ) &&
          isEqualOrNullish(
            savedSearch?.filters.monthlyExpensesInput?.maxMonthlyExpenses,
            filters.monthlyExpensesInput?.maxMonthlyExpenses
          ) &&
          isEqualOrNullish(
            savedSearch?.filters.monthlyExpensesInput?.minMonthlyExpenses,
            filters.monthlyExpensesInput?.minMonthlyExpenses
          ) &&
          isEqual(savedSearch?.filters.propertyTypes, filters.propertyTypes) &&
          isEqual(savedSearch?.filters.statuses, filters.statuses)
      )
      if (existingSavedSearchThatMatchesCurrentFilters) {
        setIsCurrentSearchSaved(true)
        setSavedSearchMatchingCurrentFilters(existingSavedSearchThatMatchesCurrentFilters)
      } else {
        setIsCurrentSearchSaved(false)
        setSavedSearchMatchingCurrentFilters(null)
      }
    }
  }, [clientIsGuest, data?.savedSearches, JSON.stringify(filters), searchLocation])

  return isCurrentSearchSaved ? (
    <RemoveSavedSearchButton savedSearchMatchingCurrentFilters={savedSearchMatchingCurrentFilters} />
  ) : (
    <ConfirmSavedSearchButton />
  )
}
